import React from "react";

/**
 * Modal that lets the user pick a render format before viewing a document.
 *
 * Props:
 *   item        – the document item (used to call onSelect)
 *   formats     – { nativeLabel, nativeTypeId, allowedFormats } | null
 *   loading     – bool
 *   error       – string | null
 *   onSelect    – (item, format) => void
 *   onClose     – () => void
 *   t           – i18next translation function (currently unused; reserved for future i18n)
 */
export default function FormatPickerModal({
  item,
  formats,
  loading,
  error,
  onSelect,
  onClose,
}) {
  const nativeLabel = formats?.nativeLabel || "Original";
  const allowedFormats = formats?.allowedFormats || [];
  const nativeTypeId = formats?.nativeTypeId || "Native";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          padding: "28px 32px",
          minWidth: 300,
          maxWidth: 420,
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontWeight: 700, fontSize: 16, color: "#1e293b" }}>
          Choose render format
        </div>

        {loading && (
          <div
            style={{
              color: "#6b7280",
              fontSize: 13,
              textAlign: "center",
              padding: "12px 0",
            }}
          >
            Please wait…
          </div>
        )}

        {error && (
          <div style={{ color: "#c0392b", fontSize: 13 }}>{error}</div>
        )}

        {!loading && !error && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Native / original format */}
            <button
              type="button"
              onClick={() => onSelect(item, "Native")}
              style={{
                padding: "11px 18px",
                borderRadius: 8,
                border: "1.5px solid #d1d5db",
                background: "#f8fafc",
                cursor: "pointer",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontWeight: 600,
                fontSize: 14,
                color: "#374151",
              }}
            >
              <span
                style={{
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  borderRadius: 4,
                  padding: "2px 7px",
                  fontSize: 12,
                  fontFamily: "monospace",
                }}
              >
                {nativeTypeId}
              </span>
              {nativeLabel}
            </button>

            {/* Configured conversion formats */}
            {allowedFormats.map((fmt) => (
              <button
                key={fmt}
                type="button"
                onClick={() => onSelect(item, fmt)}
                style={{
                  padding: "11px 18px",
                  borderRadius: 8,
                  border: "1.5px solid #bbf7d0",
                  background: "#f0fdf4",
                  cursor: "pointer",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  fontWeight: 600,
                  fontSize: 14,
                  color: "#15803d",
                }}
              >
                <span
                  style={{
                    background: "#dcfce7",
                    color: "#166534",
                    borderRadius: 4,
                    padding: "2px 7px",
                    fontSize: 12,
                    fontFamily: "monospace",
                  }}
                >
                  {fmt}
                </span>
                {fmt}
              </button>
            ))}

            {allowedFormats.length === 0 && (
              <div style={{ fontSize: 12, color: "#9ca3af" }}>
                No conversion formats configured for this database.
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          style={{
            alignSelf: "flex-end",
            padding: "7px 18px",
            borderRadius: 7,
            border: "1px solid #e5e7eb",
            background: "#f1f5f9",
            cursor: "pointer",
            fontSize: 13,
            color: "#6b7280",
            fontWeight: 500,
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
